import React from 'react';
import { useSelector } from 'react-redux';
import { useMedia } from 'react-use';
import { useTheme } from 'styled-components';

import {
  CURRENCIES,
  HANDLING_UNIQUE_NAME,
  media,
} from '@savgroup-front-common/constants';
import { getCountryTranslationSelector } from '@savgroup-front-common/core/domains/i18n/selectors';
import { formatSimpleAddressOneLine } from '@savgroup-front-common/core/formatters/address';
import { getHandlingCardLabel } from '@savgroup-front-common/core/formatters/getHandlingCardLabel/getHandlingCardLabel';
import { HandlingSummary } from '@savgroup-front-common/types';
import { ICONS_TYPE } from '@savgroup-front-common/types/Icon';

import { ClaimGroupConfirmation } from '@/api/Claim/getClaimGroupConfirmationQuery';
import {
  SimpleItemCard,
  SimpleItemCardMobile,
} from '@/view/atoms/SimpleItemCard';

import { getHandlingData } from '../../../helpers';

import { adaptDeliveryCardAddress } from './adapters/adaptDeliveryCardAddress';
import messages from './messages';

interface ResumeDeliveryProps {
  claimGroupConfirmation?: ClaimGroupConfirmation;
  handlingSummary?: HandlingSummary;
}

const ResumeDelivery: React.FC<ResumeDeliveryProps> = ({
  claimGroupConfirmation,
  handlingSummary,
}) => {
  const isTransportPlanFeatureEnabled = !!handlingSummary;
  const isMobileView = useMedia(media.maxWidth.xs);
  const theme = useTheme();
  const getCountryTranslation = useSelector(getCountryTranslationSelector);

  const addressName = claimGroupConfirmation?.deliveryAddress?.firstname;
  const fallbackAddressStreet = formatSimpleAddressOneLine(
    claimGroupConfirmation?.deliveryAddress,
    getCountryTranslation,
  );

  const { label, description } = getHandlingData({
    carrierUniqueName:
      claimGroupConfirmation?.carrierDeliveryTransportMethodUniqueName as HANDLING_UNIQUE_NAME,
  });

  const { label: newLabel, description: newDescription } = getHandlingCardLabel(
    {
      transportMethodType: handlingSummary?.delivery?.transportPlanMethodType,
    },
  );

  const delivery = handlingSummary?.delivery;

  if (isTransportPlanFeatureEnabled && delivery) {
    const cardAddress = adaptDeliveryCardAddress({
      delivery,
      fallbackAddressStreet,
      getCountryTranslation,
    });

    return isMobileView ? (
      <SimpleItemCardMobile
        icon={ICONS_TYPE.DELIVERY_ICON}
        iconColor={theme.colors.primary}
        prefixTitle={messages.deliveryTitle}
        title={newLabel}
        didactic={newDescription}
        address={cardAddress}
        price={{
          priceValue: delivery.transportPlanCostWithTax?.amount,
          priceCurrencyCode: delivery.transportPlanCostWithTax?.currency,
        }}
        isDeductibleFromRefund={delivery.isDeductible}
      />
    ) : (
      <SimpleItemCard
        icon={ICONS_TYPE.DELIVERY_ICON}
        iconColor={theme.colors.primary}
        prefixTitle={messages.deliveryTitle}
        title={newLabel}
        didactic={newDescription}
        address={cardAddress}
        price={{
          priceValue: delivery.transportPlanCostWithTax?.amount,
          priceCurrencyCode: delivery.transportPlanCostWithTax?.currency,
        }}
        isDeductibleFromRefund={delivery.isDeductible}
      />
    );
  }

  return isMobileView ? (
    <SimpleItemCardMobile
      icon={ICONS_TYPE.DELIVERY_ICON}
      iconColor={theme.colors.primary}
      prefixTitle={messages.deliveryTitle}
      title={label}
      didactic={description}
      address={{
        addressName,
        addressStreet: fallbackAddressStreet,
      }}
      price={{
        priceValue: claimGroupConfirmation?.carrierDeliveryPriceWithTax,
        priceCurrencyCode:
          claimGroupConfirmation?.carrierDeliveryPriceWithTaxCurrencyCode as CURRENCIES,
      }}
    />
  ) : (
    <SimpleItemCard
      icon={ICONS_TYPE.DELIVERY_ICON}
      iconColor={theme.colors.primary}
      prefixTitle={messages.deliveryTitle}
      title={label}
      didactic={description}
      address={{
        addressName,
        addressStreet: fallbackAddressStreet,
      }}
      price={{
        priceValue: claimGroupConfirmation?.carrierDeliveryPriceWithTax,
        priceCurrencyCode:
          claimGroupConfirmation?.carrierDeliveryPriceWithTaxCurrencyCode as CURRENCIES,
      }}
    />
  );
};

export default ResumeDelivery;
